/**
 * Advisor-portal bindings for the shared firm-branding color helpers.
 *
 * The contrast/validation logic lives in `@wavvest/shared-utils` so both
 * portals resolve `landing_color` the same way. This module only pins the
 * advisor portal's dark foreground token, which is `#1f1f24` rather than pure
 * black. That token cannot reach WCAG AA on mid-tone backgrounds, so the
 * shared helper escalates to `#000000` in that narrow band.
 */
import {
  DEFAULT_BRANDED_ACTION_SURFACE_CONTRAST_RATIO,
  getBrandedActionColors as getSharedBrandedActionColors,
  getContrastColor as getContrastColorForForegrounds,
  getVisibleBrandActionColor,
  WCAG_NON_TEXT_CONTRAST_RATIO,
  type BrandedActionColors,
} from '@wavvest/shared-utils';

export {
  getEffectiveBackgroundColor,
  isValidCssColor,
  WCAG_AA_CONTRAST_RATIO,
} from '@wavvest/shared-utils';
export type { BrandedActionColors } from '@wavvest/shared-utils';

/** The advisor portal's dark-on-light foreground token. */
export const ADVISOR_DARK_FOREGROUND = '#1f1f24';
export const TEXT_ACCENT_CONTRAST_RATIO = 4.5;
export const INTERACTIVE_SURFACE_CONTRAST_RATIO = WCAG_NON_TEXT_CONTRAST_RATIO;
export const BRANDED_ACTION_SURFACE_CONTRAST_RATIO = DEFAULT_BRANDED_ACTION_SURFACE_CONTRAST_RATIO;

export const getContrastColor = (bgColor: string, baseColor?: string): string =>
  getContrastColorForForegrounds(bgColor, baseColor, {
    dark: ADVISOR_DARK_FOREGROUND,
  });

export const getVisiblePrimaryActionColor = ({
  primaryColor,
  surfaceColor,
  pageBackgroundColor,
  minimumContrastRatio = INTERACTIVE_SURFACE_CONTRAST_RATIO,
}: {
  primaryColor: string;
  surfaceColor: string;
  pageBackgroundColor: string;
  minimumContrastRatio?: number;
}): string => {
  return getVisibleBrandActionColor({
    primaryColor,
    surfaceColor,
    pageBackgroundColor,
    minimumContrastRatio,
  });
};

export const getVisiblePrimaryTextColor = ({
  primaryColor,
  surfaceColor,
  pageBackgroundColor,
}: {
  primaryColor: string;
  surfaceColor: string;
  pageBackgroundColor: string;
}): string => {
  return getVisibleBrandActionColor({
    primaryColor,
    surfaceColor,
    pageBackgroundColor,
    minimumContrastRatio: TEXT_ACCENT_CONTRAST_RATIO,
  });
};

export const getBrandedActionColors = ({
  primaryColor,
  surfaceColor,
  pageBackgroundColor,
}: {
  primaryColor: string;
  surfaceColor: string;
  pageBackgroundColor: string;
}): BrandedActionColors => {
  return getSharedBrandedActionColors({
    primaryColor,
    surfaceColor,
    pageBackgroundColor,
    minimumSurfaceContrastRatio: BRANDED_ACTION_SURFACE_CONTRAST_RATIO,
    foregrounds: { dark: ADVISOR_DARK_FOREGROUND },
  });
};

export const getPrimaryButtonTextColor = ({
  primaryColor,
  componentBackgroundColor,
  pageBackgroundColor,
}: {
  primaryColor: string;
  componentBackgroundColor: string;
  pageBackgroundColor: string;
}): string =>
  getBrandedActionColors({
    primaryColor,
    surfaceColor: componentBackgroundColor,
    pageBackgroundColor,
  }).foregroundColor;
